import { PipelineConfig } from './config-types';
import { FrameData, LadonOutput, MaskGenerator, WorkerGenerator } from './generator';
import { logger } from './logger';
import { IFrameInferenceResult, IPipeline } from './pipeline-types';
import { BlurRenderer, LadonRenderer, PassthroughRenderer, ReplacementRenderer } from './renderer';
import { resizeImage } from './util';

/**
 * Core pipeline implementation for processing video frames with ML inference and rendering.
 * Handles frame processing, mask generation, and output rendering with proper timing controls.
 */
export class Pipeline implements IPipeline {
    /** The mask generator responsible for ML inference. */
    private generator?: MaskGenerator;

    /** The renderer responsible for compositing the final output. */
    private renderer?: LadonRenderer;

    /** Flag indicating whether the pipeline has been fully loaded and initialized. */
    private loaded = false;

    /** 2D rendering context for the input canvas used for frame processing. */
    private inputCanvasContext: CanvasRenderingContext2D;

    /** The most recent mask result from ML inference, used when skipping inference. */
    private lastMaskResult?: LadonOutput;

    /** Flag indicating whether a frame is currently being processed. */
    private isProcessingFrame = false;

    /** Promise for the currently pending frame processing operation. */
    private pendingFramePromise: Promise<IFrameInferenceResult> | null = null;

    /** Buffer for frame data copies to ensure they are not modified or garbage collected. */
    private frameDataBuffer?: Uint8ClampedArray;

    /**
     * Create a new pipeline instance.
     *
     * @param inputCanvas - Canvas element for input frame processing.
     * @param outputCanvas - Canvas element where the final rendered output will be displayed.
     * @param config - Pipeline configuration containing processing and rendering settings.
     * @param inputVideoElement - Video element serving as the input source.
     * @throws Error if the input canvas 2D context cannot be obtained.
     */
    constructor(
        private inputCanvas: HTMLCanvasElement,
        private outputCanvas: HTMLCanvasElement,
        public readonly config: PipelineConfig,
        private readonly inputVideoElement: HTMLVideoElement
    ) {
        const inputCanvasContext = this.inputCanvas.getContext('2d', {
            alpha: false,
            willReadFrequently: true,
        });

        if (!inputCanvasContext) {
            logger.errorAndThrow('Error getting input context 2d');
        }
        this.inputCanvasContext = inputCanvasContext;
    }

    /**
     * Process a single frame and return the inference result.
     * This method handles frame data, performs inference if needed, and renders the output.
     * Includes frame processing serialization to prevent overlapping operations.
     *
     * @param timestamp - The timestamp of the frame in milliseconds.
     * @param performInference - Whether to perform mask inference or reuse the last result.
     * @param frameData - Optional frame data to process instead of capturing from input.
     * @returns Promise resolving to the frame processing result with timing statistics.
     * @throws Error if the pipeline (generator or renderer) is not loaded.
     */
    public async onFrame(
        timestamp: number,
        performInference = true,
        frameData?: FrameData
    ): Promise<IFrameInferenceResult> {
        // If already processing, wait for the current frame to finish
        if (this.isProcessingFrame && this.pendingFramePromise) {
            await this.pendingFramePromise;
        }

        // Set processing flag
        this.isProcessingFrame = true;

        try {
            this.pendingFramePromise = this.processFrameInternal(
                timestamp,
                performInference,
                frameData
            );
            return await this.pendingFramePromise;
        } finally {
            this.isProcessingFrame = false;
            this.pendingFramePromise = null;
        }
    }

    /**
     * Internal frame processing implementation.
     * Handles the actual frame processing logic including timing measurements.
     *
     * @param timestamp - The timestamp of the frame in milliseconds.
     * @param performInference - Whether to perform mask inference or reuse the last result.
     * @param frameData - Optional frame data to process instead of capturing from input.
     * @returns Promise resolving to the frame processing result with timing statistics.
     * @throws Error if the pipeline (generator or renderer) is not loaded.
     */
    private async processFrameInternal(
        timestamp: number,
        performInference = true,
        frameData?: FrameData
    ): Promise<IFrameInferenceResult> {
        const frameStartTime = performance.now();

        if (!this.generator || !this.renderer) {
            logger.errorAndThrow('Pipeline is not loaded.');
        }

        let preprocessingTime = 0;
        let modelInferenceTime = 0;
        let renderFrameData: FrameData;

        if (performInference || !this.lastMaskResult) {
            // INFERENCE PATH: Capture canvas data, generate mask, render with same data
            const preprocessStart = performance.now();

            // Get canvas data for inference
            frameData ??= this.getInputFrameData();

            // Create stable copy for rendering synchronization
            renderFrameData = this.createFrameDataCopy(frameData);

            preprocessingTime = performance.now() - preprocessStart;

            // Generate mask
            const inferenceStart = performance.now();
            const rawImageData = frameData as Uint8ClampedArray;
            const maskImageData = this.getMaskImageData(rawImageData);
            await this.generator.process(maskImageData, timestamp);
            this.lastMaskResult = await this.generator.getLadonOutputForTimestamp(timestamp);
            modelInferenceTime = performance.now() - inferenceStart;
        } else {
            // SKIP INFERENCE PATH: Use video element directly for GPU optimization
            renderFrameData = this.inputVideoElement;
        }

        // Render with appropriate frame data
        const postprocessStart = performance.now();
        await this.renderer.render(renderFrameData, this.lastMaskResult);
        const postprocessingTime = performance.now() - postprocessStart;

        const frameEndTime = performance.now();
        const totalFrameTime = frameEndTime - frameStartTime;

        const result = this.createInferenceResult(timestamp, performInference);
        result.stats = {
            totalFrameTime,
            modelInferenceTime,
            preprocessingTime,
            postprocessingTime,
            frameStartTime,
            frameEndTime,
            inferenceSkipped: !performInference,
        };

        return result;
    }

    /**
     * Get the raw image data from the input canvas context.
     *
     * @returns The frame data as a Uint8ClampedArray.
     */
    private getInputFrameData(): FrameData {
        return this.inputCanvasContext.getImageData(
            0,
            0,
            this.config.input.width,
            this.config.input.height
        ).data;
    }

    /**
     * Create a copy of frame data to prevent modifications during async operations.
     */
    private createFrameDataCopy(frameData: FrameData): FrameData {
        if (frameData instanceof Uint8ClampedArray) {
            if (this.frameDataBuffer?.length !== frameData.length) {
                this.frameDataBuffer = new Uint8ClampedArray(frameData.length);
            }
            this.frameDataBuffer.set(frameData);
            return this.frameDataBuffer;
        }
        return frameData;
    }

    /**
     * Resize the image data if the model expects a different input size.
     * Handles scaling between input dimensions and model requirements.
     *
     * @param rawImageData - The raw image data to potentially resize.
     * @returns The processed mask image data, resized if necessary.
     */
    private getMaskImageData(rawImageData: Uint8ClampedArray): Uint8ClampedArray {
        const modelInput = this.config.mask.inputSize;
        const inputImage = this.config.input;

        // Check if resizing is needed based on configuration.
        if (modelInput.width === inputImage.width && modelInput.height === inputImage.height) {
            return rawImageData;
        }

        // Resize the image data.
        return resizeImage(
            rawImageData,
            inputImage.width,
            inputImage.height,
            modelInput.width,
            modelInput.height
        );
    }

    /**
     * Create the inference result object with appropriate data.
     * Handles both successful inference results and fallback cases.
     *
     * @param timestamp - The timestamp of the frame in milliseconds.
     * @param performedInference - Whether inference was actually performed for this frame.
     * @returns The inference result object containing mask data and metadata.
     */
    private createInferenceResult(
        timestamp: number,
        performedInference: boolean
    ): IFrameInferenceResult {
        if (!this.lastMaskResult) {
            return {
                facesAndLandmarks: { data: new Float32Array(0), dims: [] },
                gesture: new Float32Array(0),
                motion: 0,
                skipped: true,
                timestamp,
                // Stats will be added by the caller if needed
            };
        }

        return {
            facesAndLandmarks: this.lastMaskResult.facesAndLandmarks,
            gesture: this.lastMaskResult.gesture,
            motion: this.lastMaskResult.motion,
            skipped: !performedInference,
            timestamp,
            // Stats will be added by the caller if needed
        };
    }

    /**
     * Check if the pipeline is fully loaded and ready for processing.
     *
     * @returns True if the pipeline is loaded and ready, false otherwise.
     */
    public isLoaded(): boolean {
        return this.loaded;
    }

    /**
     * Load and initialize the pipeline components.
     * Creates the appropriate renderer and generator based on configuration.
     *
     * @returns Promise that resolves when all components are loaded and ready.
     * @throws Error if an unsupported renderer type is specified in the configuration.
     */
    public async load(): Promise<void> {
        logger.log('Creating renderer');
        switch (this.config.render.type) {
            case 'blur':
                this.renderer = new BlurRenderer(this.outputCanvas, this.config);
                break;
            case 'passthrough':
                this.renderer = new PassthroughRenderer(this.outputCanvas, this.config);
                break;
            case 'replacement':
                this.renderer = new ReplacementRenderer(this.outputCanvas, this.config);
                break;
            default:
                logger.errorAndThrow('Renderer not implemented.');
        }

        logger.log('Loading renderer');
        await this.renderer.load();

        logger.log('Creating generator');
        this.generator = new WorkerGenerator(this.config);

        logger.log('Loading generator');
        await this.generator.load();

        this.loaded = true;
    }

    /**
     * Reset the pipeline state by clearing cached mask results.
     * Useful when switching between different processing modes or sources.
     */
    public reset(): void {
        this.lastMaskResult = undefined;
    }

    /**
     * Destroy the pipeline and clean up all resources.
     * Properly disposes of the renderer and generator to prevent memory leaks.
     *
     * @returns Promise that resolves when cleanup is complete.
     */
    public async destroy(): Promise<void> {
        this.reset();
        this.renderer?.destroy();
        await this.generator?.destroy();
        this.loaded = false;
    }
}
